import { inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { exhaustMap } from 'rxjs';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { tapResponse } from '@ngrx/operators';
import { withDevtools, withStorageSync } from '@angular-architects/ngrx-toolkit';

import { withLoading } from '@r1-compass-library/store/_features/store-helpers';
import { IamRole, IamUser, IamUserSaveFormat, IamUserUpdateFormat } from '@r1-compass-library/models';
import { IamRoleService, IamUserService } from '@r1-compass-library/services';

export interface GlobalUsersStoreState {
    users: IamUser[];
    compactDivisionUsers: IamUser[];
    roles: IamRole[];
}

export const initialState: GlobalUsersStoreState = {
    users: [],
    compactDivisionUsers: [],
    roles: [],
};

// TODO - in user editing story 9453, turn this into a store in a provider, rather than global, aka component store-like
export const GlobalUsersStore = signalStore(
    { providedIn: 'root' },
    withDevtools('users'),
    withState({ ...initialState }),
    withLoading(),
    withMethods((store, userService = inject(IamUserService), rolesService = inject(IamRoleService)) => ({
        getAllUsers: rxMethod<void>(
            exhaustMap(() => {
                store.setLoading(true);
                return userService.findAll().pipe(
                    tapResponse({
                        next: users => {
                            store.setLoading(false);
                            patchState(store, { users: users });
                        },
                        error: (error: HttpErrorResponse) => console.error(error),
                    })
                );
            })
        ),
        getCompactDivisionUsers: rxMethod<void>(
            exhaustMap(() => {
                store.setLoading(true);
                return userService.findAllCompact().pipe(
                    tapResponse({
                        next: users => {
                            store.setLoading(false);
                            patchState(store, { compactDivisionUsers: users });
                        },
                        error: (error: HttpErrorResponse) => console.error(error),
                    })
                );
            })
        ),
        getAllRoles: rxMethod<void>(
            exhaustMap(() => {
                store.setLoading(true);
                return rolesService.findAll().pipe(
                    tapResponse({
                        next: roles => {
                            store.setLoading(false);
                            patchState(store, { roles: roles });
                        },
                        error: (error: HttpErrorResponse) => console.error(error),
                    })
                );
            })
        ),
        createUser: rxMethod<IamUserSaveFormat>(
            exhaustMap(user => {
                store.setLoading(true);
                return userService.create(user).pipe(
                    tapResponse({
                        next: user => {
                            store.setLoading(false);
                            patchState(store, { users: [...store.users(), user] });
                        },
                        error: (err: HttpErrorResponse) => console.error(err),
                    })
                );
            })
        ),
        updateUser: rxMethod<IamUserUpdateFormat>(
            exhaustMap(updatedUser => {
                store.setLoading(true);
                return userService.update(updatedUser.id!, updatedUser).pipe(
                    tapResponse({
                        next: user => {
                            store.setLoading(false);
                            patchState(store, {
                                users: [...store.users().filter(user => user.id !== updatedUser.id), user],
                            });
                        },
                        error: (err: HttpErrorResponse) => console.error(err),
                    })
                );
            })
        ),
        resetState() {
            patchState(store, initialState);
        },
    })),
    // withLogger('users'),
    withStorageSync({ key: 'users', storage: () => localStorage }) // TODO - for 9453 which follows 9405 - is this needed?
);

export type GlobalUsersStore = InstanceType<typeof GlobalUsersStore>;
